import styled from 'styled-components';
import {Detail, EMPHASIS, Icon} from '@forecast-it/design-system';
import {LabelledButton} from './LabelledButton';
import {TicketStatusBadge, STATUS_LABELS} from './TicketStatusBadge';
import type {TicketStatus} from '../types/ticket';

const Row = styled.div`
	display: flex;
	align-items: center;
	gap: ${p => p.theme.sys.spacing[200]};
`;

interface Props {
	status: TicketStatus;
	count: number;
	collapsed: boolean;
	/** id of the group's row container, for aria-controls. */
	contentId: string;
	onToggle: () => void;
}

/**
 * A status group's heading: a disclosure chevron, the status as a pill, and the
 * group's size. The heading carries no fill of its own — the pill is the only
 * coloured element, so a long queue reads as a list of statuses rather than a stack
 * of grey bands.
 */
export function TicketGroupHeader({status, count, collapsed, contentId, onToggle}: Props) {
	const label = STATUS_LABELS[status] ?? status;
	return (
		<Row data-testid={`group-header-${status}`}>
			<LabelledButton
				emphasis={EMPHASIS.LOW}
				label={collapsed ? `Expand ${label}` : `Collapse ${label}`}
				aria-expanded={!collapsed}
				aria-controls={contentId}
				data-testid={`group-toggle-${status}`}
				onPress={onToggle}
			>
				<Icon icon={collapsed ? 'chevronRight' : 'chevronDown'} size="s" />
			</LabelledButton>
			<TicketStatusBadge status={status} />
			{/* `medium` is Detail's auxiliary-grey key; Detail has no `auxiliary` key of
			    its own, and an unknown key resolves to no colour at all. */}
			<Detail color="medium" data-testid="group-header-count">{`(${count})`}</Detail>
		</Row>
	);
}
